import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { registerLocales } from '@app/shared/services/localeRegistration.util';
import { AssociateTokenRequest, AuthorisationService, RefreshRequest, MfaService, LogoutRequest } from '@src/app/shared/backend/mfa';
import { RetailUserRole } from '../backend/auth/RetailUserRole';
import { firstValueFrom, Observable, Subject, BehaviorSubject } from 'rxjs';
import { CompanyRequest, CompanyResponse, CompanyService, CompanySettingsRequest, CompanySettingsResponse, SettingsService, UsersService } from '../backend/clients';
import { UserRoles } from '@app/shared/enums/UserRoles.enum';
import { map } from 'rxjs/operators';
import { ThemeService } from '@src/app/shared/services/theme.service';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { DEFAULT_BRANDING_SETTINGS } from '@src/app/shared/constants/detault-theme.constants';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  private refreshTokenMinutes: number = 10;
  private refreshTimer: any;

  private isOtpTransparentLoginSubject = new BehaviorSubject<boolean>(false);
  public isOtpTransparentLogin$ = this.isOtpTransparentLoginSubject.asObservable();

  private activeRequests = 0;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private userDetailsSubject = new BehaviorSubject<any>({});
  public userDetails$: Observable<any> = this.userDetailsSubject.asObservable();

  private brandingLogoUrlSubject = new BehaviorSubject<string>('');
  public brandingLogoUrl$ = this.brandingLogoUrlSubject.asObservable();

  private sidebarLinksSubject = new BehaviorSubject<{ userManualLink: string; supportLink: string; }>({ userManualLink: '', supportLink: '' });
  public sidebarLinks$ = this.sidebarLinksSubject.asObservable();


  private featuresUpdatedSubject = new Subject<void>();
  public featuresUpdated$ = this.featuresUpdatedSubject.asObservable();

  public tokens: Promise<boolean> = new Promise<boolean>((resolve, reject) => { return false; });
  public session: any = null;

  private companySettingsLoadPromise?: Promise<CompanySettingsResponse>;
  public companySettings: CompanySettingsResponse | null = null;

  private companyDataLoadPromise?: Promise<CompanyResponse | null>;

  prefixTable: string = 'TABLE-';
  locale: string = 'en';
  rtl: boolean = false;
  companyId: any = null;
  user: any;
  brandingSettings: any = null;
  menu: any = null;
  companies: CompanyResponse[] | null | undefined = [];
  hasAccess: boolean = false;

  private _isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this._isAuthenticatedSubject.asObservable();

  private currentCompanyDataSubject = new BehaviorSubject<CompanyResponse | null>(null);
  public currentCompanyData$ = this.currentCompanyDataSubject.asObservable();

  constructor(
    private mfaService: MfaService,
    public authorisationService: AuthorisationService,
    public router: Router,
    private translate: TranslateService,
    public settingsService: SettingsService,
    public userService: UsersService,
    public companyService: CompanyService,
    private themeService: ThemeService) {

    document.body.style.overflow = '';
    this.checkAuthState();

    this.startTokenRefreshTimer();
  }

  setLoading(isLoading: boolean) {
    if (isLoading) {
      this.activeRequests++;
    } else {
      // prevent going bellow 0
      this.activeRequests = Math.max(this.activeRequests - 1, 0);
    }
    this.updateLoadingState();
  }

  getCurrentLoadingState() {
    const currentLoadingState = this.loadingSubject.getValue();
    return currentLoadingState;
  }

  private updateLoadingState() {
    this.loadingSubject.next(this.activeRequests > 0);
    this.setBodyOverflow(this.activeRequests > 0);
  }

  private setBodyOverflow(isLoading: boolean) {
    document.body.style.overflow = isLoading ? 'hidden' : '';
  }

  async getCompanyData(forceRefresh = false): Promise<CompanyResponse> {
    const company = await this.getCurrentCompanyData(forceRefresh);

    if (!company) {
      throw new Error('[SessionService] Company data is not available.');
    }

    return company;
  }

  applyTokens(authenticationResult: {
    accessToken: string;
    refreshToken: string;
    apiToken: string;
  }): void {
    const { accessToken, refreshToken, apiToken } = authenticationResult;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('apiToken', apiToken);

    const decoded = jwtDecode<any>(apiToken);

    this.session = {
      tokens: {
        apiToken: { token: apiToken, payload: decoded },
        accessToken: { token: accessToken },
        refreshToken: { token: refreshToken }
      }
    };

    this.extractUserDetails({ payload: decoded });
    this.user = {
      username: decoded['sub'] ?? decoded['cognito:username'] ?? '-',
      email: decoded['email'] ?? '-',
      id: decoded['retailinsights:id'] || '-'
    };

    this.setTokenCompanyId(decoded);
  }

  async login(username: any, password: any) {
    localStorage.removeItem('company');

    const authorisationValue = username.toLowerCase() + ':' + password;
    this.authorisationService.defaultHeaders = this.authorisationService.defaultHeaders.set('Authorization', 'Basic ' + btoa(authorisationValue));
    try {
      const response = await firstValueFrom(
        this.authorisationService.apiAuthorisationLoginPost()
      );

      const { authenticationResult, challengeName, session } = response;

      // 1. NEW PASSWORD CHALLENGE
      if (challengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          challengeName,
          session,
          userName: username
        };
      }

      // 2. SOFTWARE TOKEN MFA CHALLENGE
      if (challengeName === 'SOFTWARE_TOKEN_MFA') {
        sessionStorage.setItem('mfaSession', session);
        sessionStorage.setItem('mfaUsername', username);
        return {
          challengeName,
          session
        };
      }

      // 3. MFA ENROLMENT (QR Code setup)
      if (challengeName === 'MFA_ENROLMENT') {
        const accessToken = authenticationResult?.accessToken;
        if (!accessToken) {
          throw new Error('MFA_ENROLMENT requires accessToken');
        }

        const associatePayload: AssociateTokenRequest = {
          token: accessToken
        };

        const mfaAssociate = await firstValueFrom(this.mfaService.apiMfaAssociatePost(associatePayload));

        if (mfaAssociate?.url) {
          // qr Content & confif Key
          sessionStorage.setItem('mfaQrUrl', mfaAssociate.url);

          if (mfaAssociate?.mfaConfigurationKey) {
            sessionStorage.setItem('mfaConfigKey', mfaAssociate.mfaConfigurationKey);
          }

          sessionStorage.setItem('mfaToken', accessToken);

          this.router.navigate(['/otp/setup']);
        } else {
          console.error('Failed to initiate MFA association');
          throw new Error('MFA enrolment failed.');
        }

        return;
      }

      // 4. SUCCESSFUL AUTHENTICATION
      if (!authenticationResult?.accessToken || !authenticationResult.apiToken) {
        throw new Error('Login failed: No tokens returned');
      }

      this.applyTokens(authenticationResult);
      this.hasAccess = true;

      await this.getUser();
      this.startTokenRefreshTimer();

      return response;

    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }

  async getUser() {
    this.setLoading(true);
    try {
      const rawToken = localStorage.getItem('apiToken');
      if (rawToken) {
        const decoded = jwtDecode<any>(rawToken);

        if (decoded) {
          this.extractUserDetails({ payload: decoded });

          const userId = decoded['retailinsights:id'];

          if (!userId) {
            console.warn('Missing user ID in token');
            return;
          }

          const userResponse = await firstValueFrom(this.userService.apiUsersProfileGet());

          this.user = userResponse;
          this.setLanguageFromUserProfile();

        } else {
          console.warn('Invalid token payload');
          this.user = null;
        }
      } else {
        console.warn('No ID token found in localStorage');
        this.user = null;
      }
    } catch (error) {
      console.error('Error extracting user from token:', error);
      this.user = null;
    } finally {
      this.setLoading(false);
      return this.user;
    }
  }

  isAdmin$(): Observable<boolean> {
    return this.userDetails$.pipe(
      map((userDetails) => {
        if (!userDetails || !userDetails.userRoles) {
          return false;
        }
        return userDetails.userRoles == UserRoles.ADMIN;
      })
    );
  }

  isSuperAdmin$(): Observable<boolean> {
    return this.userDetails$.pipe(
      map((userDetails) => {
        if (!userDetails || !userDetails.userRoles) {
          return false;
        }

        return userDetails.userRoles == UserRoles.SUPER_ADMIN;
      })
    );
  }

  async getSession(): Promise<any> {
    try {
      const apiToken = localStorage.getItem('apiToken');
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (apiToken && accessToken) {
        const decodedapiToken = jwtDecode<any>(apiToken);
        this.session = {
          tokens: {
            apiToken: { token: apiToken, payload: decodedapiToken },
            accessToken: { token: accessToken },
            refreshToken: { token: refreshToken }
          }
        };

        this.setTokenCompanyId(decodedapiToken);
        await this.getCurrentCompanyData(); // cached + deduped
        await this.getCompanySettings();

        return this.session;
      } else {
        this.session = null;
        return null;
      }
    } catch (error) {
      console.error('Error building session from stored tokens:', error);
      throw error;
    }
  }

  private extractUserDetails(apiToken: any) {
    if (apiToken) {
      const payload = apiToken.payload;
      const isOtpTransparentLogin = payload['retailinsights:otp'] === 'true' || payload['retailinsights:otp'];
      const userDetails = {
        userId: payload['retailinsights:id'] || '',
        email: payload['email'] || '',
        firstName: payload['retailinsights:first_name'] || '',
        lastName: payload['retailinsights:last_name'] || '',
        privacyPolicyAccepted: payload['privacy_policy'] || '',
        emailVerified: payload['email_verified'] || '',
        company: payload['retailinsights:company'] || '',
        currentCompany: payload['retailinsights:current_company'] || '',
        assignedRetailers: JSON.parse(payload['retailinsights:retailers'] || '[]'),
        userRoles: payload['retailinsights:role'],
        userRoleName: payload['retailinsights:role_name'],
        features: JSON.parse(payload['retailinsights:features'] || '{}'),
        actions: JSON.parse(payload['retailinsights:actions'] || '{}'),
        landingPage: payload['retailinsights:landing_page'] || 'Profile',
      };

      this.setTokenCompanyId(payload);
      this.isOtpTransparentLoginSubject.next(isOtpTransparentLogin);
      this.userDetailsSubject.next(userDetails);
    }
  }

  getUserDetails() {
    return this.userDetailsSubject.getValue();
  }

  getCurrentCompanyDetails(): CompanyResponse | null {
    return this.currentCompanyDataSubject.getValue();
  }

  async getCurrentCompanyData(forceRefresh = false): Promise<CompanyResponse | null> {
    const cached = this.currentCompanyDataSubject.getValue();

    // return cached data if exists and is not a force refresh
    if (!forceRefresh && cached) {
      return cached;
    }

    // reuse any in-flight request
    if (this.companyDataLoadPromise) {
      return this.companyDataLoadPromise;
    }

    this.companyDataLoadPromise = (async () => {
      if (!this.companyId) return null;

      const company = await firstValueFrom(this.companyService.apiCompanyIdGet(this.companyId));
      this.setCurrentCompanyData(company);

      return company;
    })().finally(() => {
      this.companyDataLoadPromise = undefined;
    });

    return this.companyDataLoadPromise;
  }

  async loadCurrentCompanyData(): Promise<void> {
    try {
      await this.getCurrentCompanyData(true);
    } catch (error) {
      console.error('Failed to load company data:', error);
      this.currentCompanyDataSubject.next(null);
    }
  }

  async GetPermissions(resource: string): Promise<RetailUserRole[]> {
    const apiToken = localStorage.getItem('apiToken');
    const payload = apiToken ? jwtDecode<any>(apiToken) : null;
    const rawValues = payload ? payload[resource] : {};

    let values;
    if (typeof rawValues === 'string') {
      try {
        values = JSON.parse(rawValues);
      } catch (e) {
        console.error('Failed to parse values:', e);
        values = {};
      }
    } else {
      values = rawValues;
    }

    const rolePermissions: RetailUserRole[] = values ? JSON.parse(JSON.stringify(values)) : [];
    return rolePermissions;
  }

  async setFeatures(values: any[]) {

    this.companySettings?.features?.forEach(v => {
      v.isEnabled = false;
    });

    values.forEach(v => {
      const feature = this.companySettings?.features?.find(f => f.id == v);
      if (feature) {
        feature.isEnabled = true;
      }
    });

    const data: CompanySettingsRequest = {
      features: JSON.parse(JSON.stringify(this.companySettings?.features))
    };

    await firstValueFrom(this.settingsService.apiSettingsSystemUpdatePut(data));

    // refresh sidebar notifying subscribers
    this.featuresUpdatedSubject.next();
  }


  async setSpecialFeatures(data: any) {
    await firstValueFrom(this.companyService.apiCompanyUpdateIdPut(this.companyId, data));
  }

  getFeaturesList() {
    return this.companySettings?.features ?? [];
  }


  getSpecialFeaturesList() {
    return this.companySettings;
  }

  getFeatures(): any[] {
    const features: any[] = [];
    this.companySettings?.features?.forEach((v: any) => {
      if (v.isEnabled) {
        features.push(v.id.toString());
      }
    });
    return features;
  }

  private setTokenCompanyId(decoded: any) {
    this.companyId = localStorage.getItem('company') ?? decoded['retailinsights:current_company'] ?? decoded['retailinsights:company'];
  }

  async setCompanyId(selectedOption: any) {
    this.companyId = selectedOption;
    localStorage.setItem('company', selectedOption);

    const value = localStorage.getItem('refreshToken'); //await this.refreshToken();
    const refreshToken: string = value != null ? value.toString() : '';

    const identity = await firstValueFrom(this.userService.apiUsersSwitchCompanyPut({
      companyId: this.companyId,
      refreshToken: refreshToken
    }));

    localStorage.setItem('apiToken', identity?.authenticationResult?.apiToken ?? '');
    this.session.tokens.apiToken.token = identity?.authenticationResult?.apiToken;

    await this.loadCurrentCompanyData();

    window.location.reload();
  }

  getCompany(): any {
    return this.companyId;
  }

  async getCompanySettings(forceRefresh = false): Promise<CompanySettingsResponse> {

    // return cached data
    if (!forceRefresh && this.companySettings) {
      return this.companySettings;
    }

    // prevent multiple calls
    if (!forceRefresh && this.companySettingsLoadPromise) {
      return this.companySettingsLoadPromise;
    }

    this.companySettingsLoadPromise = (async () => {

      // Get current company once
      const company = await this.getCurrentCompanyData();

      // Fetch from API
      const settings = await firstValueFrom(this.settingsService.apiSettingsReadGet());

      this.companySettings = settings;
      this.companySettings?.features?.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);

      // Get all enabled languages
      const enabledLanguages: string[] = (settings.languages ?? [])
        .filter(lang => lang.isEnabled && lang.code)
        .map(lang => lang.code as string);
      registerLocales(enabledLanguages);

      // use default branding if not set a custom theme
      this.brandingSettings = settings.brandingSettings || { ...DEFAULT_BRANDING_SETTINGS };
      this.themeService.loadTheme(this.brandingSettings, company?.name);
      this.setBrandingLogoUrl(this.brandingSettings?.logoUrl ?? null);

      return settings;

    })().finally(() => {
      // Clear promise
      this.companySettingsLoadPromise = undefined;
    });

    return this.companySettingsLoadPromise;
  }

  getBrandingSettings(): any {
    return this.brandingSettings;
  }

  getUserRoleName(): Observable<any> {
    return this.userDetails$.pipe(
      map(userDetails => userDetails.userRoleName)
    );
  }

  getFirstName(): Observable<string> {
    return this.userDetails$.pipe(
      map((details) => details.firstName)
    );
  }

  getLastName(): Observable<string> {
    return this.userDetails$.pipe(
      map((details) => details.lastName)
    );
  }

  getUserId(): Observable<string> {
    return this.userDetails$.pipe(
      map((details) => details.userId)
    );
  }

  getEmail(): Observable<string> {
    return this.userDetails$.pipe(
      map((details) => details.email)
    );
  }

  getPrivacyPolicyAccepted(): Observable<string> {
    return this.userDetails$.pipe(
      map((details) => details.privacyPolicyAccepted)
    );
  }

  getcompanyId() {
    return this.companyId;
  }

  async getCompanies(forceRefresh: boolean = false) {
    const userDetails = this.userDetailsSubject.getValue();
    const isSuperAdmin = userDetails?.userRoles === UserRoles.SUPER_ADMIN;
    if (!isSuperAdmin) {
      console.warn('Access denied, the current user cannot get the companies list.');
      return [];
    }

    if (forceRefresh || this.companies?.length == 0) {
      const result = await firstValueFrom(this.companyService.apiCompanyFilterComboPost({
        pageSize: 500,
        pageNumber: 1
      }));

      this.companies = result.results;
    }

    return this.companies;
  }

  async refreshToken(): Promise<any> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken || refreshToken.trim() === '') {
      console.warn('[SessionService] Token refresh skipped: no valid refresh token.');
      return null;
    }

    console.info('[SessionService] Token refresh started.', {
      startedAt: new Date().toISOString()
    });

    try {
      const refreshRequest: RefreshRequest = { token: refreshToken };

      const response = await firstValueFrom(
        this.authorisationService.apiAuthorisationRefreshPost(refreshRequest)
      );

      if (response?.authenticationResult) {
        const { accessToken, apiToken, refreshToken: newRefreshToken } = response.authenticationResult;

        console.info('[SessionService] Token refresh response received.', {
          hasAccessToken: !!accessToken,
          hasApiToken: !!apiToken,
          hasRefreshToken: !!newRefreshToken,
          receivedAt: new Date().toISOString()
        });

        this.applyTokens({
          accessToken,
          refreshToken: newRefreshToken,
          apiToken
        });

        console.info('[SessionService] Token refreshed successfully.');

        this.startTokenRefreshTimer();

        return this.session;
      } else {
        console.warn('[SessionService] Invalid token refresh response:', response);
        return null;
      }

    } catch (error) {
      console.error('[SessionService] Failed to refresh token:', error);
      return null;
    }
  }

  startTokenRefreshTimer() {
    // avoid overlapping timers
    clearTimeout(this.refreshTimer);

    const apiToken = localStorage.getItem('apiToken');

    if (!apiToken) {
      console.info('[SessionService] Token refresh timer not started: no API token found.');
      return;
    }

    try {
      const decoded: any = jwtDecode(apiToken);

      const refreshDelay = this.refreshTokenMinutes * 60 * 1000;
      const nextRefreshAt = new Date(Date.now() + refreshDelay);

      console.info('[SessionService] Token refresh timer scheduled.', {
        tokenExpiresAt: new Date(decoded.exp * 1000).toISOString(),
        refreshDelayMinutes: this.refreshTokenMinutes,
        nextRefreshAt: nextRefreshAt.toISOString()
      });

      if (refreshDelay > 0) {
        this.refreshTimer = setTimeout(async () => {
          console.info('[SessionService] Token refresh timer triggered.', {
            triggeredAt: new Date().toISOString()
          });

          const refreshedSession = await this.refreshToken();

          if (!refreshedSession) {
            console.warn('[SessionService] Token auto-refresh failed. Logging out user.');
            await this.logout(true);
          }
        }, refreshDelay);
      } else {
        console.warn('[SessionService] Token refresh timer not scheduled: invalid refresh delay.');
      }
    } catch (error) {
      console.error('[SessionService] Error decoding token for refresh timer:', error);
    }
  }

  clearSession() {
    this.session = null;
    this.userDetailsSubject.next({});
    this.isOtpTransparentLoginSubject.next(false);
    this._isAuthenticatedSubject.next(false);

    this.companySettings = null;
    this.companySettingsLoadPromise = undefined;
    this.brandingSettings = null;

    this.companyDataLoadPromise = undefined;
    this.currentCompanyDataSubject.next(null);
    this.setSidebarLinks(null);
  }

  resetLocalSession() {
    clearTimeout(this.refreshTimer);

    localStorage.clear();

    this.hasAccess = false;// reset completed login flag

    this.clearSession();
  }

  async logout(redirectAfterLogout: boolean = false) {
    const redirectUrl = this.getLogoutRedirectUrl();

    try {

      // stop any scheduled token refresh
      clearTimeout(this.refreshTimer);

      this.hasAccess = false;
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {

        const decoded: any = jwtDecode(accessToken);
        const currentTime = Math.floor(Date.now() / 1000);
        const isExpired = decoded?.exp && decoded.exp < currentTime;

        if (!isExpired) {
          const logoutRequest: LogoutRequest = {
            token: accessToken
          };

          try {
            await firstValueFrom(this.authorisationService.apiAuthorisationLogoutPost(logoutRequest));
          } catch (error) {
            console.error('Backend logout failed or was unauthorized', error);
          }
        }
      }

      // Clear all localStorage
      localStorage.clear();

      this.clearSession();
      this._isAuthenticatedSubject.next(false);

      if (redirectAfterLogout) {
        window.location.replace(redirectUrl);
      }

    } catch (error) {
      console.error('Logout failed', error);

      if (redirectAfterLogout) {
        window.location.replace(redirectUrl);
      }
    }
  }

  async isLogged(): Promise<boolean> {
    try {
      const apiToken = localStorage.getItem('apiToken');

      if (!apiToken) {
        this._isAuthenticatedSubject.next(false);
        return false;
      }

      const decoded = jwtDecode(apiToken);
      const currentTime = Math.floor(Date.now() / 1000);

      // exp is in seconds since epoch
      const isExpired = decoded?.exp && decoded.exp < currentTime;
      const isAuthenticated = !isExpired;

      if (isAuthenticated) {
        this.session = {
          tokens: {
            apiToken: { token: apiToken, payload: decoded },
            accessToken: { token: localStorage.getItem('accessToken') },
            refreshToken: { token: localStorage.getItem('refreshToken') }
          }
        };
        this.extractUserDetails({ payload: decoded });
      } else {
        this.clearSession();
      }

      this._isAuthenticatedSubject.next(isAuthenticated);
      return isAuthenticated;

    } catch (error) {
      console.error('Failed to check login state:', error);
      this._isAuthenticatedSubject.next(false);
      return false;
    }
  }

  private async checkAuthState(): Promise<void> {
    await this.isLogged();
  }

  private setLanguageFromUserProfile() {
    let userLangCode = this.user?.language?.code;

    if (!userLangCode && this.user?.languageId && this.companySettings?.languages) {
      const matchedLang = this.companySettings.languages.find(lang => lang.id === this.user.languageId);
      userLangCode = matchedLang?.code;
    }

    // fallback
    const langToUse = userLangCode || 'en';
    this.translate.use(langToUse);
    this.locale = langToUse;
    this.rtl = ['ar'].includes(langToUse);
  }

  clearTableStateKeys(): void {
    const cacheKeyPrefixes = ['paginator::', 'filters-panel::', 'sort-panel::'];

    Object.keys(localStorage).forEach((key) => {
      const shouldRemove = cacheKeyPrefixes.some(cacheKey => key.startsWith(cacheKey));

      if (shouldRemove) {
        localStorage.removeItem(key);
      }
    });
  }

  setBrandingLogoUrl(url: string | null): void {
    const finalUrl = url ?? '';
    this.brandingSettings = { ...(this.brandingSettings ?? {}), logoUrl: finalUrl };
    this.brandingLogoUrlSubject.next(finalUrl);
  }

  setSidebarLinks(userManualLink?: string | null, supportLink?: string | null): void {
    this.sidebarLinksSubject.next({
      userManualLink: userManualLink ?? '',
      supportLink: supportLink ?? ''
    });
  }

  setCurrentCompanyData(company: CompanyResponse | null): void {
    this.currentCompanyDataSubject.next(company);
    this.setSidebarLinks(company?.userManualLink, company?.supportLink);
  }

  isOtpTransparentLogin(): boolean {
    return this.isOtpTransparentLoginSubject.getValue();
  }

  isOtpTransparentLoginSession(): boolean {
    if (this.isOtpTransparentLogin()) {
      return true;
    }

    const apiToken = localStorage.getItem('apiToken');

    if (!apiToken) {
      return false;
    }

    try {
      const decoded = jwtDecode<any>(apiToken);
      return decoded['retailinsights:otp'] === 'true';
    } catch (error) {
      console.error('Failed to decode token while checking OTP session:', error);
      return false;
    }
  }

  getLogoutRedirectUrl(): string {
    return this.isOtpTransparentLoginSession() ? '/embedded' : '/login';
  }
}
